import { Link } from "react-router-dom";
import DataDisclaimer from "../../components/DataDisclaimer/DataDisclaimer";
import Layout from "../../components/Layout/Layout";

const About = () => {
  return (
    <Layout>
      <section className="max-w-3xl mx-auto px-6 py-16 w-full">
        <p className="text-sun font-semibold uppercase tracking-wide text-sm">
          A project of Earthbound Martian
        </p>
        <h1 className="text-5xl font-bold text-alt-blk mt-3">About Lunamalies</h1>
        <p className="text-xl text-alt-blk/80 mt-6 leading-relaxed">
          Lunamalies is a public archive of <em>catalogued</em> lunar reports —
          Transient Lunar Phenomena (TLP) from published astronomy catalogs.
          It is not a UAP database. The name is Luna + anomalies.
        </p>

        <h2 className="text-2xl font-bold mt-12 mb-4">Provisional data</h2>
        <DataDisclaimer />

        <h2 className="text-2xl font-bold mt-12 mb-4">Why this exists</h2>
        <p className="leading-relaxed mb-4">
          Historical TLP reports are observational records: colored glows,
          mists, brightenings, and darkenings noted by astronomers from the 6th
          century onward. They usually get folded into general UAP collections,
          where the lunar context is easy to lose.
        </p>
        <p className="leading-relaxed">
          Lunamalies keeps the published catalogs queryable and on a Moon map.
          The tone is archival, not sensational. We record what observers
          reported and where they said they saw it. We do not promote an
          explanation. When a published study confirms, revises, or discredits
          a report (and we are{" "}
          <Link to="/contact" className="text-turquoise hover:text-mars">
            informed about it
          </Link>
          ), we will annotate that on the sighting. Until then the
          catalogs stand as observers wrote them.
        </p>

        <h2 className="text-2xl font-bold mt-12 mb-4">Sources in v1</h2>
        <p className="leading-relaxed mb-4">
          The first catalog is{" "}
          <a
            className="text-turquoise hover:text-mars"
            href="https://ntrs.nasa.gov/citations/19680018720"
            target="_blank"
            rel="noreferrer"
          >
            Middlehurst, Burley, Moore, and Welther 1968, NASA Technical Report
            R-277
          </a>
          , <em>Chronological Catalog of Reported Lunar Events</em> (~576 reports,
          1540–1967). That report is a U.S. government publication.
        </p>
        <p className="leading-relaxed mb-4">
          Also ingested: Cameron 1978 (NASA TM-79399 / NSSDC 78-03), ~1,348
          reports from Document Intelligence on the published scan (pages 27–114),
          and Cameron’s 2006 catalog extension (~470 additional reports) from the
          machine-readable Aberystwyth PDF. Rows are provisional — OCR and
          table-stitching will leave gaps and digit errors. Copies via{" "}
          <a
            className="text-turquoise hover:text-mars"
            href="https://users.aber.ac.uk/atc/tlp/tlp.htm"
            target="_blank"
            rel="noreferrer"
          >
            Dr. Tony Cook (Aberystwyth / ALPO / BAA)
          </a>
          . ALPO and BAA observers have spent decades on repeat-illumination
          checks of those catalogs; a cleaner, re-weighted list exists but is
          held for refereed publication. We will not scrape unpublished
          research data.
        </p>
        <h2 className="text-2xl font-bold mt-12 mb-4">What’s next (v2)</h2>
        <p className="leading-relaxed">
          v1 is read-only. v2 will take observer submissions, with moderation
          so new rows stay cited, archival, and neutral. Until that opens,
          catalog corrections and source suggestions go through{" "}
          <Link to="/contact" className="text-turquoise hover:text-mars">
            Contact
          </Link>
          .
        </p>

        <h2 className="text-2xl font-bold mt-12 mb-4">How locations work</h2>
        <p className="leading-relaxed mb-4">
          Catalogs name features more often than they give coordinates. We match
          location text to IAU named features and store selenographic latitude
          and longitude (east-positive). Reports that only say “dark side” or
          “whole moon” stay unlocated.
        </p>
        <p className="leading-relaxed">
          Phenomenon types follow Winifred Cameron’s five classes (gaseous,
          reddish, blue/green/violet, brightening, darkening). For TR R-277 those
          labels are inferred from the description. Cameron 1978 rows use her
          published class letters.
        </p>

        <h2 className="text-2xl font-bold mt-12 mb-4">Earthbound Martian</h2>
        <p className="leading-relaxed mb-6">
          Lunamalies is built by Earthbound Martian as a tool for the stargazing
          and remote-travel community. If you observe from dark skies, the
          Landships are for that kind of work.
        </p>
        <div className="flex flex-wrap gap-3">
          <a
            href="https://earthboundmartian.com"
            className="inline-block bg-mars hover:bg-sunset text-white px-8 py-4 rounded-2xl font-semibold"
          >
            Visit Earthbound Martian
          </a>
          <Link
            to="/contact"
            className="inline-block border border-alt-blk px-8 py-4 rounded-2xl font-semibold hover:bg-alt-blk hover:text-white transition-colors"
          >
            Contact
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default About;
